import { useState } from "react";

import {
  Banner,
  EmptyState,
  ErrorState,
  NotificationList,
  PageHead,
  SkeletonList,
  type NotificationEntry,
} from "@ui/react";
import { auth } from "../../lib/firebase";
import { captureError } from "../../lib/errors";
import { RouterLink } from "./NotificationBell";
import { FEED_LIMIT, markRead, toEntry, useNotifications } from "./notifications";

const PAGE_SIZE = FEED_LIMIT;

/** `/notifications` — every notification, newest first, with bulk mark-read. */
export function NotificationsScreen() {
  const [max, setMax] = useState(PAGE_SIZE);
  // Renamed from the hook's own `failed` (listener failure) so it doesn't
  // clash with this page's `failed` — a single row's mark-read failing.
  const { items, loading, failed: loadFailed } = useNotifications(max);
  const uid = auth().currentUser?.uid;
  const [failed, setFailed] = useState(false);

  const unread = items.filter((item) => !item.read).length;
  const entries = items.map(toEntry);

  // Mirrors the user site's single-row failure banner (U14.8): the read write
  // is cosmetic and must never block navigation, but a person who tapped a
  // notification and watched its "New" pill fail to clear deserves to know.
  function onOpen(entry: NotificationEntry) {
    if (!uid || entry.read) return;
    return markRead(uid, [entry.id]).catch((error) => {
      captureError("Could not mark notification read", error);
      setFailed(true);
    });
  }

  return (
    <>
      <PageHead
        eyebrow="Notifications"
        title="What's happened"
        lead={unread > 0 ? `${unread} unread` : "Updates about your jobs and bookings, newest first."}
      />
      <p className="sr-only" role="status">
        {loading ? "Loading notifications" : `${items.length} notifications, ${unread} unread`}
      </p>
      {failed ? (
        <Banner tone="warn">We couldn't mark that as read, but you haven't missed anything.</Banner>
      ) : null}
      {loading ? (
        <SkeletonList rows={3} height="4rem" />
      ) : loadFailed ? (
        <ErrorState
          title="We couldn't load your notifications"
          message="Check your connection and try again."
          onRetry={() => window.location.reload()}
        />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing yet">
          Updates about your jobs and bookings will show up here.
        </EmptyState>
      ) : (
        <NotificationList
          entries={entries}
          onOpen={onOpen}
          // This route is behind the authenticated app shell, so a signed-in
          // uid is guaranteed by the time there is anything here to mark.
          onMarkRead={(ids) => markRead(uid!, ids)}
          onShowMore={entries.length >= max ? () => setMax((m) => m + PAGE_SIZE) : undefined}
          renderLink={RouterLink}
        />
      )}
    </>
  );
}
